import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import type { Badge, BadgeColor } from '../LibraryDeps'
import { BADGE_COLORS, BADGE_NAME_MAX, BADGES_MAX, nextColor, type BadgeCatalog } from './badges'

export type BadgeState = 'on' | 'off' | 'mixed'

interface Props {
  catalog: BadgeCatalog
  /** Whether the item (or every / some of the selected items) carries the badge. */
  stateOf: (badge: Badge) => BadgeState
  /** A tick: saved straight away by the caller. */
  onToggle: (badge: Badge, on: boolean) => void
  onClose: () => void
  /** Create, rename, recolour, delete — Vault write. */
  canManage: boolean
  title?: string
  /** Fixed viewport position (a grid tile clips anything inside it). */
  style?: CSSProperties
}

/**
 * The Badge dropdown: tick several (each tick saves), type to search or to create a
 * new one with a colour, ✎ a row to rename / recolour / delete it.
 */
export function BadgeMenu({ catalog, stateOf, onToggle, onClose, canManage, title, style }: Props) {
  const { t } = useTranslation('library')
  const [q, setQ] = useState('')
  const [color, setColor] = useState<BadgeColor | null>(null)
  const [editing, setEditing] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (editing !== null) setEditing(null)
      else onClose()
    }
    // Capture: the viewer's own Esc (close) must not fire while the menu is open.
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose, editing])

  const name = q.replace(/\s+/g, ' ').trim()
  const shown = useMemo(() => {
    const key = name.toLocaleLowerCase()
    return key ? catalog.badges.filter((b) => b.name.toLocaleLowerCase().includes(key)) : catalog.badges
  }, [catalog.badges, name])
  const exact = catalog.badges.find((b) => b.name.toLocaleLowerCase() === name.toLocaleLowerCase())
  const full = catalog.badges.length >= BADGES_MAX
  const canCreate = canManage && Boolean(name) && !exact && !full
  const newColor = color ?? nextColor(catalog.badges)

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try {
      await work()
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('error'))
    } finally {
      setBusy(false)
    }
  }
  const create = () =>
    run(async () => {
      const b = await catalog.create(name, newColor)
      onToggle(b, true)
      setQ('')
      setColor(null)
    })

  return (
    <>
      <div className="lib-backdrop" onClick={(e) => (e.stopPropagation(), onClose())} />
      <div
        className="lib-pop lib-menu lib-badge-menu"
        role="menu"
        aria-label={title ?? t('vault.badges.label')}
        style={style}
        onClick={(e) => e.stopPropagation()}
      >
        {title && <span className="lib-menu-head">{title}</span>}
        <input
          className="lib-badge-search"
          value={q}
          autoFocus
          maxLength={BADGE_NAME_MAX}
          placeholder={canManage ? t('vault.badges.searchOrCreate') : t('vault.badges.search')}
          aria-label={canManage ? t('vault.badges.searchOrCreate') : t('vault.badges.search')}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            if (exact) onToggle(exact, stateOf(exact) !== 'on')
            else if (canCreate && !busy) void create()
          }}
        />
        <div className="lib-badge-list">
          {shown.map((b) =>
            editing === b.id ? (
              <BadgeEditRow key={b.id} badge={b} catalog={catalog} onDone={() => setEditing(null)} />
            ) : (
              <BadgeRow
                key={b.id}
                badge={b}
                state={stateOf(b)}
                onToggle={() => onToggle(b, stateOf(b) !== 'on')}
                onEdit={canManage ? () => setEditing(b.id) : undefined}
              />
            ),
          )}
          {!shown.length && !canCreate && (
            <span className="lib-badge-empty">{catalog.badges.length ? t('vault.badges.noMatch') : t('vault.badges.none')}</span>
          )}
        </div>
        {canCreate && (
          <div className="lib-badge-create">
            <button type="button" role="menuitem" className="lib-menu-item" disabled={busy} onClick={() => void create()}>
              <FeatherIcon name="plus" size={14} />
              <span className="lib-menu-label">{t('vault.badges.create', { name })}</span>
              <span className="lib-badge-dot" data-c={newColor} />
            </button>
            <Swatches value={newColor} onPick={setColor} />
          </div>
        )}
        {canManage && full && !exact && name && <span className="lib-badge-empty">{t('vault.badges.full', { max: BADGES_MAX })}</span>}
        {error && <p className="ofp-error lib-badge-error">{error}</p>}
      </div>
    </>
  )
}

function BadgeRow({ badge, state, onToggle, onEdit }: { badge: Badge; state: BadgeState; onToggle: () => void; onEdit?: () => void }) {
  const { t } = useTranslation('library')
  return (
    <div className="lib-badge-row">
      <button
        type="button"
        role="menuitemcheckbox"
        aria-checked={state === 'mixed' ? 'mixed' : state === 'on'}
        className="lib-menu-item"
        onClick={onToggle}
      >
        <span className={`lib-badge-box ${state !== 'off' ? 'is-on' : ''}`} aria-hidden="true">
          {state === 'on' ? '✓' : state === 'mixed' ? '–' : ''}
        </span>
        <span className="lib-badge" data-c={badge.color}>
          {badge.name}
        </span>
      </button>
      {onEdit && (
        <button type="button" className="lib-icon-btn lib-badge-edit" aria-label={t('vault.badges.edit', { name: badge.name })} title={t('vault.badges.editShort')} onClick={onEdit}>
          <FeatherIcon name="edit-2" size={13} />
        </button>
      )}
    </div>
  )
}

function BadgeEditRow({ badge, catalog, onDone }: { badge: Badge; catalog: BadgeCatalog; onDone: () => void }) {
  const { t } = useTranslation('library')
  const [name, setName] = useState(badge.name)
  const [color, setColor] = useState<BadgeColor>(badge.color)
  const [confirm, setConfirm] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const clean = name.replace(/\s+/g, ' ').trim()

  const run = async (work: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try {
      await work()
      onDone()
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : t('error'))
      setBusy(false)
    }
  }
  const save = () => {
    const patch: { name?: string; color?: BadgeColor } = {}
    if (clean !== badge.name) patch.name = clean
    if (color !== badge.color) patch.color = color
    if (!Object.keys(patch).length) return onDone()
    void run(() => catalog.update(badge.id, patch))
  }

  if (confirm) {
    return (
      <div className="lib-badge-editrow" role="group" aria-label={t('vault.badges.deleteTitle', { name: badge.name })}>
        <span className="lib-confirm-title">{t('vault.badges.deleteTitle', { name: badge.name })}</span>
        <span className="lib-confirm-body">{t('vault.badges.deleteBody', { count: badge.count })}</span>
        {error && <p className="ofp-error">{error}</p>}
        <div className="lib-confirm-actions">
          <button type="button" className="lib-btn" disabled={busy} onClick={() => setConfirm(false)}>
            {t('common.cancel')}
          </button>
          <button type="button" className="lib-btn lib-btn--danger" disabled={busy} onClick={() => void run(() => catalog.remove(badge.id))}>
            {t('vault.badges.delete')}
          </button>
        </div>
      </div>
    )
  }
  return (
    <div className="lib-badge-editrow" role="group" aria-label={t('vault.badges.edit', { name: badge.name })}>
      <input
        className="lib-badge-search"
        value={name}
        autoFocus
        maxLength={BADGE_NAME_MAX}
        aria-label={t('vault.badges.name')}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && clean && !busy) {
            e.preventDefault()
            save()
          }
        }}
      />
      <Swatches value={color} onPick={setColor} />
      {error && <p className="ofp-error">{error}</p>}
      <div className="lib-confirm-actions">
        <button type="button" className="lib-btn lib-btn--ghost lib-badge-del" disabled={busy} onClick={() => setConfirm(true)}>
          <FeatherIcon name="trash-2" size={13} />
          {t('vault.badges.delete')}
        </button>
        <span className="lib-spacer" />
        <button type="button" className="lib-btn" disabled={busy} onClick={onDone}>
          {t('common.cancel')}
        </button>
        <button type="button" className="lib-btn lib-btn--primary" disabled={busy || !clean} onClick={save}>
          {t('vault.badges.save')}
        </button>
      </div>
    </div>
  )
}

function Swatches({ value, onPick }: { value: BadgeColor; onPick: (c: BadgeColor) => void }) {
  const { t } = useTranslation('library')
  return (
    <div className="lib-swatches" role="radiogroup" aria-label={t('vault.badges.color')}>
      {BADGE_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          role="radio"
          aria-checked={c === value}
          aria-label={t(`vault.badges.colors.${c}`)}
          title={t(`vault.badges.colors.${c}`)}
          className={`lib-swatch ${c === value ? 'is-on' : ''}`}
          data-c={c}
          onClick={() => onPick(c)}
        />
      ))}
    </div>
  )
}
